/**
 * projeTaahhutnameService.js — reads what a project's taahhütname PDF needs and hands the finished file to the browser (backlog #666).
 * The page itself is drawn by projeTaahhutnamePdf.js; this file only talks to the backend and the browser.
 *
 * One taahhütname (isg | elektrik | statik) of one LIVE project, in one language:
 *   - GET /projects/{id}/taahhutnameler?languageId=1|2 (#216 / #665): the header (fair, company, decorator, hall, stand), the chosen source
 *     file and the wording in force at the project's fair (or the one the decorator approved). English gets Turkish wording until an English
 *     version is published: text.languageId says so, and the caller is told (`metinTurkce`) so the screen can say it.
 *   - the chosen source file's bytes: /public/files/project_files/<storedFileName> is permitAll with CORS open (PublicFileController), so
 *     fetch() reads it directly. JPG / PNG go in as they are, WebP is drawn through a canvas into PNG, a PDF contributes its first page.
 *     An organizer (IZLEME) gets no storedFileName, so it never reaches the PDF.
 *   - the fair logo, through the same loader as the Firma İSG Taahhütnamesi (#541 / #661).
 * The PDF is offered only when the source is present (state CHOSEN / COMPANY_APPROVED / SIGNED); `pdfHazirMi` is that test.
 */
import i18n from "../i18n";
import { getTaahhutnameler, getProjectFileUrl } from "./projectService";
import { LANGUAGE } from "./fuarService";
import {
  dosyaBaytlari, webpMi, webpYiPngYap, fuarSatiri, fuarLogosuOku, isgTaahhutneVarliklariniYukle, pdfDosyasiniIndir,
} from "./isgTaahhutnameService";
import { resimTuru } from "./isgTaahhutnamePdf";
import { projeTaahhutnamePdfUret, projeTaahhutnameDosyaAdi } from "./projeTaahhutnamePdf";

export { PROJE_TAAHHUTNAME_DILLERI } from "./projeTaahhutnamePdf";

const hataMetni = (anahtar, degerler) => i18n.t(`pdf:projeTaahhutname.ui.${anahtar}`, degerler);

const pdfMi = (b) => !!b && b.length > 5 && b[0] === 0x25 && b[1] === 0x50 && b[2] === 0x44 && b[3] === 0x46; // "%PDF"

/** True when the taahhütname has a chosen source the caller may read and a wording to print. */
export const pdfHazirMi = (taahhut) => !!taahhut?.source?.storedFileName && !!taahhut?.text;

/**
 * The chosen source file as bytes the generator embeds: { bytes, tur: "png" | "jpg" | "pdf" }.
 * @throws Error (UI language) when it cannot be read or is not a JPG / PNG / WebP / PDF.
 */
export async function kaynakBelgesiniOku(source) {
  const ad = source.sourceName || source.originalFileName || source.storedFileName;
  let bytes;
  try {
    bytes = await dosyaBaytlari(getProjectFileUrl(source.storedFileName));
  } catch (e) {
    throw new Error(hataMetni("kaynakOkunamadi", { ad }));
  }
  if (webpMi(bytes)) {
    try {
      bytes = await webpYiPngYap(bytes);
    } catch (e) {
      throw new Error(hataMetni("kaynakOkunamadi", { ad }));
    }
  }
  const tur = pdfMi(bytes) ? "pdf" : resimTuru(bytes);
  if (!tur) throw new Error(hataMetni("kaynakTuruDesteklenmiyor", { ad }));
  return { bytes, tur };
}

/**
 * Everything the PDF of one taahhütname is made from, read for the language it will be made in.
 * @returns {Promise<{veri: object, uyarilar: {logoSorunu: boolean, metinTurkce: boolean}}>} `veri` is what projeTaahhutnamePdfUret takes.
 * @throws Error (UI language) when the project has no source chosen / no wording for this one, or the source cannot be read.
 */
export async function projeTaahhutnameVerisiOku({ projectId, key, dil }) {
  const languageId = dil === "en" ? LANGUAGE.EN : LANGUAGE.TR;
  const d = await getTaahhutnameler(projectId, languageId);
  const taahhut = (d?.taahhutnameler || []).find((x) => x.key === key);
  if (!taahhut?.source?.storedFileName) throw new Error(hataMetni("kaynakYok"));
  if (!taahhut.text) throw new Error(hataMetni("metinYok"));
  const [kaynak, logoSonucu] = await Promise.all([
    kaynakBelgesiniOku(taahhut.source),
    d.fuarId ? fuarSatiri(d.fuarId, LANGUAGE.TR).then(fuarLogosuOku) : { logo: null, logoSorunu: false },
  ]);
  return {
    veri: { key, header: d.header || {}, text: taahhut.text, kaynak, logo: logoSonucu.logo },
    uyarilar: {
      logoSorunu: logoSonucu.logoSorunu,
      metinTurkce: dil === "en" && Number(taahhut.text.languageId) !== LANGUAGE.EN,
    },
  };
}

/** Reads, builds and downloads the PDF; answers the warnings of projeTaahhutnameVerisiOku. */
export async function projeTaahhutnameIndir({ projectId, key, dil }) {
  const { veri, uyarilar } = await projeTaahhutnameVerisiOku({ projectId, key, dil });
  const bytes = await projeTaahhutnamePdfUret(veri, dil, await isgTaahhutneVarliklariniYukle("tr"));
  pdfDosyasiniIndir(bytes, projeTaahhutnameDosyaAdi(veri, dil));
  return uyarilar;
}
